import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import DashboardCard from 'areas/dashboard/components/DashboardCard';
import { StyledIconButton } from 'common/components/button/StyledButton';
import { getSelectedAssessmentPeriodId } from 'common/selectors/auth';
import { PageSection } from 'common/styles/page';
import { useApiPortalV1AssessmentsResultsListQuery } from 'areas/assessment-schedule/api';

import { AggregateType } from 'common/constants/AggregateType';
type Confidence = {
  start: number;
  end: number;
};

type Detail = {
  department: string;
  score: number;
  change: string;
};

type RowData = {
  question: string;
  overall_score: number;
  change: string;
  confidence_interval: Confidence;
  details: Detail[];
};

interface PillarsTableProps {
  selectedPillar: number | null;
  selectedCategory: string | null;
}

const PillarsTable: React.FC<PillarsTableProps> = ({ selectedPillar, selectedCategory }) => {
  const [expandedRows, setExpandedRows] = useState<{ [key: number]: boolean }>({});

  const toggleRow = (index: number) => {
    setExpandedRows(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const toggleAllRows = () => {
    const allExpanded = questionData.length > 0 && questionData.every((_, index) => expandedRows[index]);
    const nextExpandedRows = questionData.reduce(
      (acc, _, index) => ({ ...acc, [index]: !allExpanded }),
      {} as { [key: number]: boolean }
    );
    setExpandedRows(nextExpandedRows);
  };

  const assessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);

  const { data: data_by_category } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.CATEGORY_QUESTION_SCORES,
    assessmentPeriodId: assessmentPeriodId,
  });
  const { data: data_by_option } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.OPTION_QUESTION_SCORES,
    assessmentPeriodId: assessmentPeriodId,
  });

  const [questionData, setQuestionData] = useState<RowData[]>([]);

  useEffect(() => {
    if (
      data_by_category &&
      data_by_category.length > 0 &&
      data_by_option &&
      data_by_option.length > 0
    ) {
      const filteredCategoryData = data_by_category[0]?.result?.filter(
        (score: { factor: number; category_id: string; question: string; overall_score: number }) =>
          score.factor === selectedPillar && score.category_id === selectedCategory
      );

      const filteredOptionData = data_by_option[0]?.result?.filter(
        (score: { factor: number; category_id: string; question: string; overall_score: number }) =>
          score.factor === selectedPillar && score.category_id === selectedCategory
      );

      const questions: RowData[] = Array.from(
        new Map<string, RowData>(
          filteredCategoryData?.map(
            (score: {
              question: string;
              overall_score: number;
              confidence_interval: Confidence;
            }) => [
              score.question,
              {
                question: score.question,
                overall_score: score.overall_score,
                change: '0',
                confidence_interval: score.confidence_interval,
                details: [],
              },
            ]
          )
        ).values()
      );

      questions.forEach(question => {
        filteredOptionData
          .filter((score: { question: string }) => score.question === question.question)
          .map((score: { option_name: string; overall_score: number }) => {
            question.details.push({
              department: score.option_name,
              score: score.overall_score,
              change: '0',
            });
          });
      });
      setQuestionData(questions);
    }
  }, [data_by_category, data_by_option, selectedCategory, selectedPillar, assessmentPeriodId]);

  return (
    <DashboardCard
      title="Questions Details"
      controls={
        <StyledIconButton
          icon={questionData.length > 0 && questionData.every((_, index) => expandedRows[index]) ? 'minus' : 'plus'}
          title={questionData.length > 0 && questionData.every((_, index) => expandedRows[index]) ? 'Collapse all question rows' : 'Expand all question rows'}
          onClick={toggleAllRows}
          color="grey"
        />
      }
    >
      <PageSection
        display="flex"
        flexDirection="column"
        justifyContent="center"
        style={{ width: '100%', height: '80%' }}
      >
        {questionData.map((row, index) => (
          <div key={index}>
            <Row onClick={() => toggleRow(index)}>
              <QuestionColumn>{row.question}</QuestionColumn>
              <ScoreColumn>
                <strong>
                  {row.overall_score.toFixed(1)}
                  {/*  ({row.change}) */}
                </strong>
              </ScoreColumn>
              <BarColumn>
                {index === 0 && (
                  <Legend>
                    {Array.from(Array(11).keys()).map(num => (
                      <span key={num}>{num}</span>
                    ))}
                  </Legend>
                )}
                <Bar>
                  <ConfidenceBar
                    start={(row.confidence_interval.start / 10) * 100}
                    width={
                      ((row.confidence_interval.end - row.confidence_interval.start) / 10) * 100
                    }
                  />
                  <ScoreMarker position={(row.overall_score / 10) * 100} />
                </Bar>
              </BarColumn>
            </Row>
            <SubTable className={expandedRows[index] ? 'expanded' : ''}>
              {row.details.map((detail, idx) => (
                <Row key={idx}>
                  <OptionColumn>{detail.department}</OptionColumn>
                  <BarColumn>
                    <DepartmentBar style={{ width: `${(detail.score / 10) * 100}%` }}>
                      <DepartmentLabel>
                        {detail.score.toFixed(1)} {/*  ({detail.change}) */}
                      </DepartmentLabel>
                    </DepartmentBar>
                  </BarColumn>
                </Row>
              ))}
            </SubTable>
          </div>
        ))}
      </PageSection>
    </DashboardCard>
  );
};

export default PillarsTable;

const Row = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid #ccc;
  padding: 10px 0;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover {
    background: #f9f9f9;
  }
`;

const QuestionColumn = styled.div`
  flex: 4;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  padding-right: 20px;
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 20px;
  max-width: 800px;
  color: #555;
`;

const ScoreColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  padding-right: 20px;
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 20px;
  max-width: 800px;
  color: #555;
`;

const OptionColumn = styled.div`
  flex: 5;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-right: 35px;
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  max-width: 800px;
  color: #777;
`;

const BarColumn = styled.div`
  flex: 8;
  position: relative;
`;

const SubTable = styled.div`
  margin-top: 10px;
  max-height: 0;
  overflow: hidden;
  transition: max-height 1s ease;

  &.expanded {
    max-height: 1000px; /* Large enough to fit the content */
  }
`;

const Bar = styled.div`
  background: #e0e0e0;
  height: 20px;
  position: relative;
  margin: 5px 0;
  border-radius: 5px;
`;

const ConfidenceBar = styled.div<{ start: number; width: number }>`
  position: absolute;
  background: #f88;
  height: 20px;
  left: ${props => props.start}%;
  width: ${props => props.width}%;
  border-radius: 5px;
  transition:
    left 0.5s ease,
    width 0.5s ease;
`;

const ScoreMarker = styled.div<{ position: number }>`
  position: absolute;
  background: #fff;
  border: 1px solid #777;
  border-radius: 50%;
  width: 15px;
  height: 15px;
  top: 50%;
  left: ${props => props.position}%;
  transform: translate(-50%, -50%);
  transition: left 0.5s ease;
`;

const DepartmentBar = styled.div`
  background: #a5cfee;
  height: 20px;
  position: relative;
  margin: 5px 0;
  border-radius: 5px;
  transition:
    left 0.5s ease,
    width 0.5s ease;
`;

const DepartmentLabel = styled.div`
  position: absolute;
  left: calc(100% + 10px);
  font-size: 12px;
  color: #333;
  white-space: nowrap;
`;

const Legend = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  margin-top: 18px;
  margin-bottom: 14px;
  color: #666;
  width: 100%;
  position: relative;
`;

